import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ModernButton } from '../ui/ModernButton';
import { ImportResultList } from './ImportResultList';
import jobListingsService from '../../services/JobListingsService';
import { importZip } from '../../services/zipImport';

/**
 * The 20 MB ceiling belongs to the CMS endpoint, and only the loose pair still
 * goes there whole. A ZIP is unpacked here and uploaded file by file, so its
 * own size is bounded by what the browser can hold, not by that endpoint. A
 * full 2026 export is around 15 MB and would have failed its own limit.
 */
const MAX_PAIR_BYTES = 20 * 1024 * 1024;
const MAX_ZIP_BYTES = 200 * 1024 * 1024;

function FilePicker({ label, hint, accept, file, onPick }) {
  return (
    <label className="block cursor-pointer">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
        {label}
      </span>
      <div
        className={`rounded-xl border-2 border-dashed px-4 py-5 text-center transition-colors ${
          file
            ? 'border-emerald-400 bg-emerald-50/60 dark:border-emerald-700 dark:bg-emerald-900/20'
            : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 bg-slate-50 dark:bg-slate-800/60'
        }`}
      >
        <input
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0] || null)}
        />
        {file ? (
          <span className="font-mono text-xs text-emerald-700 dark:text-emerald-300 break-all">
            {file.name}
          </span>
        ) : (
          <span className="text-sm text-slate-500 dark:text-slate-400">{hint}</span>
        )}
      </div>
    </label>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        active
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Upload dialog for new job campaigns.
 *
 * Two ways in, both landing on the same CMS endpoint: a ZIP with any number of
 * pairs, or one explicit HTML + JSON pair. A pair is always both files — the CMS
 * needs the HTML for the campaign page and the JSON for the structured data.
 */
export function AddJobsModal({ onClose, onImported }) {
  const [mode, setMode] = useState('pair');
  const [zipFile, setZipFile] = useState(null);
  const [htmlFile, setHtmlFile] = useState(null);
  const [jsonFile, setJsonFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const [progress, setProgress] = useState(null);

  const canSubmit = mode === 'zip' ? Boolean(zipFile) : Boolean(htmlFile && jsonFile);

  const oversized =
    mode === 'zip'
      ? (zipFile?.size || 0) > MAX_ZIP_BYTES
      : (htmlFile?.size || 0) + (jsonFile?.size || 0) > MAX_PAIR_BYTES;

  async function handleUpload() {
    if (!canSubmit || oversized) return;
    setUploading(true);
    try {
      const result =
        mode === 'zip'
          ? await importZip(zipFile, setProgress)
          : await jobListingsService.importPair(htmlFile, jsonFile);

      if (!result.success) {
        toast.error(result.error);
        return;
      }

      setReport(result.data);
      const { summary } = result.data;
      if (summary.failed === 0) {
        toast.success(`${summary.created} neu · ${summary.updated} aktualisiert`);
      } else {
        toast.error(`${summary.failed} von ${summary.total} Dateien fehlgeschlagen`);
      }
      onImported?.();
    } finally {
      setUploading(false);
    }
  }

  function resetForNextUpload() {
    setReport(null);
    setZipFile(null);
    setHtmlFile(null);
    setJsonFile(null);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              Neue Stellen hinzufügen
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Import landet immer als Entwurf im CMS.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Schließen"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pb-6">
          {report ? (
            <>
              <ImportResultList report={report} />
              <div className="mt-5 flex justify-end gap-2">
                <ModernButton variant="secondary" onClick={resetForNextUpload}>
                  Weitere hochladen
                </ModernButton>
                <ModernButton onClick={onClose}>Fertig</ModernButton>
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-1 p-1 mb-5 rounded-xl bg-slate-100 dark:bg-slate-800 w-fit">
                <TabButton active={mode === 'pair'} onClick={() => setMode('pair')}>
                  Einzelne Stelle
                </TabButton>
                <TabButton active={mode === 'zip'} onClick={() => setMode('zip')}>
                  ZIP-Archiv
                </TabButton>
              </div>

              {mode === 'pair' ? (
                <div className="space-y-4">
                  <FilePicker
                    label="Kampagnenseite (HTML)"
                    hint="NN_Name.html auswählen"
                    accept=".html,.htm"
                    file={htmlFile}
                    onPick={setHtmlFile}
                  />
                  <FilePicker
                    label="Google-Jobs-Daten (JSON)"
                    hint="NN_Name_GoogleJobs.json auswählen"
                    accept=".json"
                    file={jsonFile}
                    onPick={setJsonFile}
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Beide Dateien werden benötigt: das HTML wird als Kampagnenseite ausgeliefert,
                    die JSON füllt Titel, Beschreibung und die Google-Jobs-Felder.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <FilePicker
                    label="ZIP-Archiv"
                    hint="ZIP mit HTML- und JSON-Paaren auswählen"
                    accept=".zip"
                    file={zipFile}
                    onPick={setZipFile}
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Jede Stelle braucht <code>NN_Name.html</code> und{' '}
                    <code>NN_Name_GoogleJobs.json</code>; Fragebogen 2, Einladungs-Mail, Indeed-,
                    AfA-Text und Storyboard gleichen Namens kommen automatisch mit.
                  </p>
                </div>
              )}

              {progress && progress.total > 0 && (
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                  {progress.current
                    ? `Stelle ${progress.done + 1} von ${progress.total}: ${progress.current}`
                    : `${progress.total} von ${progress.total} verarbeitet`}
                </p>
              )}

              {oversized && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {mode === 'zip' ? 'ZIP größer als 200 MB.' : 'Upload größer als 20 MB. Bitte aufteilen.'}
                </p>
              )}

              <div className="mt-6 flex justify-end gap-2">
                <ModernButton variant="secondary" onClick={onClose}>
                  Abbrechen
                </ModernButton>
                <ModernButton
                  onClick={handleUpload}
                  disabled={!canSubmit || oversized || uploading}
                  loading={uploading}
                >
                  Importieren
                </ModernButton>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
